import React, { useEffect } from 'react';
import Router from 'next/router';
import { styled } from '@components-shared/src/stitches';
import { MINIMIZED_PLAYER_HEIGHT } from '@components/AudioPlayerMobile/MinimizedPlayer';
import { Spinner } from '@components/basics/Spinner';
import { Button } from '@components/main-components/button/Button';
import { NextFutureImage } from '@components/main-components/image/NextFutureImage';
import { MOBILE_NAV_HEIGHT } from '@components/main-components/mobile-nav/constants';
import { useConnectedAuth } from '@context/auth/connected';
import { useMediaQuery } from '@context/mediaQuery';
import { trackClickEvent } from '@lib/analytics';
import { useAudioController } from '@store/audio/AudioController';

const ASSUMED_HEADER_HEIGHT = 72;

const RIGHT_FIGURE = '/images/not_connected_right_figure.png';
const RIGHT_FIGURE_WIDTH = 146;
const RIGHT_FIGURE_HEIGHT = 132;

const LEFT_FIGURE = '/images/not_connected_left_figure.png';
const LEFT_FIGURE_WIDTH = 128;
const LEFT_FIGURE_HEIGHT = 107;

export default function SignIn() {
  const { isAuthenticatedWithWallet, isConnectingWallet, login } = useConnectedAuth();
  const { tablet } = useMediaQuery();
  const { activeTrackId } = useAudioController();

  function onClick() {
    trackClickEvent({
      event: 'Sign in Clicked',
      properties: {
        sourceComponent: 'Sign in page',
      },
    });
    login();
  }

  useEffect(() => {
    if (isConnectingWallet || isAuthenticatedWithWallet || tablet) {
      Router.push('/');
    }
  }, [isAuthenticatedWithWallet, tablet, isConnectingWallet]);

  if (isConnectingWallet || isAuthenticatedWithWallet || tablet) {
    return (
      <Container center>
        <Spinner size="lg" />
      </Container>
    );
  }

  return (
    <Container withAudioPlayer={!!activeTrackId}>
      <Section direction="right">
        <NextFutureImage
          src={RIGHT_FIGURE}
          alt="right figure"
          height={RIGHT_FIGURE_HEIGHT}
          width={RIGHT_FIGURE_WIDTH}
          quality={100}
          priority
        />
      </Section>
      <Section>
        <Heading>
          Your sounds.
          <br />
          Your space.
        </Heading>

        <Subheading>
          Sync your playlists, follows,
          <br />
          favorites, and collection.
        </Subheading>

        <ButtonWrapper>
          <Button label="Sign in" onClick={onClick} variant="primary" />
        </ButtonWrapper>
      </Section>
      <Section direction="left">
        <NextFutureImage
          src={LEFT_FIGURE}
          alt="left figure"
          height={LEFT_FIGURE_HEIGHT}
          width={LEFT_FIGURE_WIDTH}
          quality={100}
          priority
        />
      </Section>
    </Container>
  );
}

const Container = styled('div', {
  width: '100%',
  height: `calc(100dvh - ${ASSUMED_HEADER_HEIGHT}px - ${MOBILE_NAV_HEIGHT}px)`,
  background: '$white',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  flexDirection: 'column',
  paddingBottom: 24,

  variants: {
    center: {
      true: {
        justifyContent: 'center',
      },
    },
    withAudioPlayer: {
      true: {
        height: `calc(100dvh - ${MINIMIZED_PLAYER_HEIGHT}px - ${ASSUMED_HEADER_HEIGHT}px - ${MOBILE_NAV_HEIGHT}px)`,
      },
    },
  },
});

const Section = styled('div', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'column',
  width: '100%',
  gap: 12,

  variants: {
    direction: {
      right: {
        alignItems: 'flex-end',
      },
      left: {
        alignItems: 'flex-start',
      },
    },
  },
});

const Heading = styled('h2', {
  title: 'XL',
  textAlign: 'center',
  color: '$black',
});

const Subheading = styled('span', {
  base: 'M',
  textAlign: 'center',
  fontWeight: 400,
  color: '$base700',
});

const ButtonWrapper = styled('div', {
  paddingTop: 12,
});
